import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { storageGet, storageSet } from "@/lib/uiStorage";
import { CURRENCIES, formatMoney } from "@/config/pricing";

/**
 * Lumora currency context — fixed price tables, no FX maths.
 *
 * Region comes from the server (`GET /api/geo`: edge geo header → cached IP
 * lookup), falling back to the browser locale. Australian visitors are locked
 * to AUD (inc GST). Everyone else defaults to USD and can switch to NZD.
 * At checkout the backend picks the price from the billing country, so the
 * selector only affects what is displayed.
 */
const STORAGE_KEY = "lumora_currency_v3";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROW_OPTIONS = ["usd", "nzd"];

const CurrencyContext = createContext(null);

function localeHint() {
  if (typeof navigator === "undefined") return "en-US";
  return navigator.language || "en-US";
}

function fallbackFromLocale() {
  const lang = localeHint().toLowerCase();
  if (lang.endsWith("-au")) return { region: "AU", currency: "aud", locked: true, options: ["aud"], detected: false };
  if (lang.endsWith("-nz")) return { region: "NZ", currency: "nzd", locked: false, options: ROW_OPTIONS, detected: false };
  return { region: "ROW", currency: "usd", locked: false, options: ROW_OPTIONS, detected: false };
}

export function CurrencyProvider({ children }) {
  const [geo, setGeo] = useState(() => fallbackFromLocale());
  const [currency, setCurrencyState] = useState(() => {
    const stored = storageGet(STORAGE_KEY);
    return ROW_OPTIONS.includes(stored) ? stored : fallbackFromLocale().currency;
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/geo`, { params: { hint: localeHint() } })
      .then((r) => {
        if (cancelled || !r?.data?.currency) return;
        setGeo(r.data);
        if (r.data.locked) setCurrencyState("aud");
        else setCurrencyState((cur) => (r.data.options.includes(cur) ? cur : r.data.currency));
      })
      .catch(() => { /* locale fallback already applied */ });
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((code) => {
    const c = String(code || "").toLowerCase();
    if (geo.locked || !geo.options.includes(c)) return;
    setCurrencyState(c);
    storageSet(STORAGE_KEY, c);
  }, [geo]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    region: geo.region,
    locked: !!geo.locked,
    detected: !!geo.detected,
    options: geo.options,
    meta: CURRENCIES[currency] || CURRENCIES.usd,
    format: (amount) => (amount === 0 ? "Free" : formatMoney(amount, currency)),
    localeHint: localeHint(),
  }), [currency, setCurrency, geo]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    const fb = fallbackFromLocale();
    return { currency: fb.currency, setCurrency: () => {}, region: fb.region, locked: fb.locked, detected: false,
             options: fb.options, meta: CURRENCIES[fb.currency], format: (a) => (a === 0 ? "Free" : formatMoney(a, fb.currency)), localeHint: localeHint() };
  }
  return ctx;
}
